import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { EM_DASH } from '@/lib/format/number';
import styles from './StatBlock.module.css';

interface StatBlockProps {
  label: string;
  /**
   * Pre-formatted value. Pass `null` for "not recorded" — the block renders "—" and
   * mutes it. Canvas 1a: "Nulo vira '—'. A ausência é informação."
   */
  value: string | null;
  /** Sample or explanation under the number, e.g. "6 de 18". */
  caption?: ReactNode;
  /** 0–100. Draws the hairline proportion bar. Omit when the value is not a share. */
  pct?: number | null;
  onInk?: boolean;
  /**
   * Marks this as *the* number of the screen. Canvas 1a allows exactly one gold per screen,
   * so a second `gold` block on the same page is a design bug, not a styling choice.
   */
  gold?: boolean;
}

export function StatBlock({
  label,
  value,
  caption,
  pct,
  onInk = false,
  gold = false,
}: StatBlockProps) {
  const isNull = value == null;
  return (
    <div className={cx(styles.block, onInk && styles.onInk, gold && !isNull && styles.gold)}>
      <div className={styles.label}>{label}</div>
      <div className={cx(styles.value, isNull && styles.null)}>{isNull ? EM_DASH : value}</div>
      {pct != null && !isNull && (
        <div className={styles.bar}>
          <div
            className={styles.barFill}
            style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
          />
        </div>
      )}
      {caption != null && <div className={styles.caption}>{caption}</div>}
    </div>
  );
}

/** Lays blocks out in a row with the hairline dividers the canvas uses. */
export function StatRow({
  children,
  scroll = false,
  className,
}: {
  children: ReactNode;
  /**
   * Blocos com largura própria, e a faixa rola de lado em vez de espremê-los.
   *
   * Mesmo arranjo do `PillGroup`: a classe utilitária `scroll-x` cuida da rolagem, e o CSS daqui
   * solta o `flex` dos filhos. Sem ela, `.row > .block { flex: 1 }` encolhe os blocos até o rótulo
   * e o número quebrarem — e o `overflow-x` nunca chega a valer.
   */
  scroll?: boolean;
  className?: string;
}) {
  return (
    <div className={cx(styles.row, scroll && styles.rowScroll, scroll && 'scroll-x', className)}>
      {children}
    </div>
  );
}
